import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { MapPin, X } from 'lucide-react';
import { projectsGallery } from '../mock';

const Galerie = () => {
  const [filter, setFilter] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);

  const types = ['all', 'Permis de construire', 'Déclaration préalable', 'Permis d\'aménager'];

  const filteredProjects = filter === 'all' 
    ? projectsGallery 
    : projectsGallery.filter(project => project.type === filter);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-pink-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-4 mb-4">
            <h1 className="text-5xl font-bold text-gray-900">
              Galerie de <span className="text-pink-600">projets réalisés</span>
            </h1>
            <video src="https://customer-assets.emergentagent.com/job_7bec0238-1e03-4c93-afc1-e7baf1238116/artifacts/nk3b9zei_logo-U2C.mp4" autoPlay loop muted playsInline className="h-24 w-auto flex-shrink-0 hidden sm:block rounded-lg" />
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Découvrez quelques-uns des projets que j'ai accompagnés avec succès
          </p>
          <div className="pt-4" style={{animation: 'pulseScale 2s ease-in-out infinite'}}>
            <Link to="/contact" className="inline-block px-8 py-3 bg-pink-600 hover:bg-pink-700 text-white text-xl font-bold rounded-full shadow-lg hover:shadow-xl transition-all duration-300">
              Etude de faisabilité offerte pour votre projet !!
            </Link>
          </div>
          <style>{`@keyframes pulseScale { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.08); } }`}</style>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-6 px-4 sm:px-6 lg:px-8 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-3 justify-center">
            {types.map((type) => (
              <button
                key={type}
                onClick={() => setFilter(type)}
                className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 ${
                  filter === type
                    ? 'bg-pink-600 text-white shadow-lg scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {type === 'all' ? 'Tous les projets' : type}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 text-white hover:text-pink-400 transition-colors"
          >
            <X className="w-8 h-8" />
          </button>
          <div className="max-w-7xl max-h-full">
            <img
              src={selectedImage.image}
              alt={selectedImage.title}
              className="max-w-full max-h-[90vh] object-contain"
            />
            <div className="text-white text-center mt-4">
              <h3 className="text-2xl font-bold mb-2">{selectedImage.title}</h3>
              <p className="text-gray-300">{selectedImage.description}</p>
            </div>
          </div>
        </div>
      )}

      {/* Projects Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <Card 
                key={project.id} 
                className="overflow-hidden border-2 border-gray-200 hover:border-pink-300 hover:shadow-2xl transition-all duration-300 group cursor-pointer"
                onClick={() => setSelectedImage(project)}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-pink-600 text-white">
                      {project.type}
                    </Badge>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <p className="text-white text-sm leading-relaxed">
                      {project.description}
                    </p>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {project.title}
                  </h3>
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <div className="flex items-center space-x-2">
                      <MapPin className="w-4 h-4 text-pink-600" />
                      <span>{project.location}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg">Aucun projet trouvé pour ce filtre.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Votre projet, notre expertise
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Faites appel à URBA-2C pour donner vie à votre projet d'urbanisme
          </p>
          <a
            href="/contact"
            className="inline-flex items-center px-8 py-4 bg-pink-600 hover:bg-pink-700 text-white font-semibold rounded-lg shadow-lg transition-all duration-300 hover:scale-105"
          >
            Commencer mon projet
          </a>
        </div>
      </section>
    </div>
  );
};

export default Galerie;